export const SEARCH_APPLICANTS = gql`
  query SearchApplicants(
    $pageInfo: PageInfoInput
    $filters: ApplicantFiltersInput
  ) {
    applicants(pageInfo: $pageInfo, filters: $filters) {
      totalCount
      items {
        applicantId
        firstName
        middleName
        lastName
        email
        phone
        yearsOfExperience
        callCount
        hiringStage
        applicantStage
        jobListing {
          title
        }
      }
      pageInfo {
        offset
        limit
      }
    }
  }
`

export const GET_APPLICANTS = gql`
  query Applicants(
    $pageInfo: PageInfoInput
    $filters: ApplicantFiltersInput
    $columns: [ApplicantColumnInput!]
    $sortBy: [ApplicantSortByInput!]
    $stepFilters: StepFiltersInput
  ) {
    applicants(
      pageInfo: $pageInfo
      filters: $filters
      columns: $columns
      sortBy: $sortBy
    ) {
      totalCount
      items {
        applicantId
        firstName
        middleName
        lastName
        email
        yearsOfExperience
        callCount
        missedOutboundCallCount
        applicationDate
        hiredDate
        terminatedDate
        disqualifiedDate
        notInterestedDate
        lastActionDate
        lastContactDate
        isCommunicationOptIn
        automationsEnabled
        phone
        createdAt
        updatedAt
        hiringStage
        applicantStage
        notInterestedReason
        disqualifiedReason
        terminatedReason
        tags {
          tagId
          label
          status
        }
        recruiter {
          employeeId
          firstName
          lastName
          status
        }
        memberEmployees {
          employeeId
          firstName
          lastName
          status
        }
        jobListing {
          jobListingId
          title
        }
        address {
          city
          state
        }
        application {
          availableForWork
          trackingLink {
            trackingLinkId
            linkType
            linkName
          }
        }
        steps(filters: $stepFilters, pageInfo: { limit: 100, offset: 0 }) {
          items {
            stepId
            type
            status
            customStepId
            customStep {
              label
            }
          }
          totalCount
        }
        tasks {
          totalCount
          items {
            taskId
            title
            description
            createdAt
            updatedAt
            status
            priority
            assignees {
              employeeId
              firstName
              lastName
            }
          }
        }
        bookings(filters: { upcomingOnly: true }) {
          totalCount
          items {
            bookingId
            bookingStartTime
            bookingEndTime
            bookingDurationInMinutes
            title
            status
          }
        }
        expirationTrackers {
          totalCount
          items {
            expirationTrackerId
            category
            expirationDate
            documentId
          }
        }
        customFieldValues {
          applicantCustomFieldValueId
          customFieldId
          stringValue
          booleanValue
          dateValue
          selectValues
          customField {
            customFieldId
            key
            label
            fieldType
            status
            options {
              label
              value
              sortOrder
            }
          }
        }
      }
      pageInfo {
        offset
        limit
      }
    }
  }
`

// Lean by design: the sidebar checklist subtree (steps → documents →
// documentIntelligence) lives in GET_SIDEBAR_CHECKLIST_STEPS
// (steps.graphql.tsx) so this query stays fast — it backs the applicant
// header/sidebar and is refetched after most applicant mutations.
export const GET_APPLICANT_BY_ID = gql`
  query Applicant($id: String!) {
    applicant(id: $id) {
      applicantId
      firstName
      middleName
      lastName
      email
      yearsOfExperience
      callCount
      applicationDate
      lastActionDate
      phone
      createdAt
      updatedAt
      hiringStage
      applicantStage
      applicantStageDate
      isCommunicationOptIn
      notInterestedReason
      disqualifiedReason
      terminatedReason
      eligibleForRehire
      isPermanentlyDisqualified
      status
      automationsEnabled
      primaryApplicantId
      mergeJobsAsDuplicate {
        id
        status
        primaryApplicant {
          applicantId
          firstName
          lastName
          fullName
        }
      }
      archivedAt
      archivedBy {
        employeeId
        firstName
        lastName
      }
      tags {
        tagId
        label
        status
      }
      jobListing {
        jobListingId
        title
        type
        jobListingTemplate {
          jobListingTemplateId
          steps {
            stepType
            customStepId
            order
          }
        }
        trackingLinks {
          trackingLinkId
          linkType
          linkName
        }
      }
      recruiter {
        employeeId
        firstName
        lastName
        jobTitle
        roleType
        status
      }
      memberEmployees {
        employeeId
        firstName
        lastName
        status
      }
      application {
        dob
        ssn
        applicationId
        trackingLink {
          trackingLinkId
          linkType
          linkName
        }
        license {
          licenseNumber
          state
          licenseClass
          endorsements
          expirationDate
          medicalExamExpirationDate
          isLicenseSuspended
        }
        accidents {
          createdAt
          accidentDate
          state
        }
        employmentExperiences {
          employmentExperienceId
          createdAt
          updatedAt
          startDate
          endDate
          companyName
        }
      }
      address {
        city
        state
        zipCode
      }
      customFieldValues {
        applicantCustomFieldValueId
        stringValue
        booleanValue
        dateValue
        selectValues
        updatedAt
        customField {
          customFieldId
          key
          label
          fieldType
          isRequired
          isSearchable
          status
          sortOrder
          options {
            optionId
            label
            value
            sortOrder
          }
        }
      }
    }
  }
`

export const GET_APPLICANT_NAME_BY_ID = gql`
  query ApplicantName($id: String!) {
    applicant(id: $id) {
      firstName
      middleName
      lastName
    }
  }
`

export const FIND_APPLICANT_COMPANY_FOR_SWITCH = gql`
  query FindApplicantCompanyForSwitch($applicantId: String!) {
    findApplicantCompanyForSwitch(applicantId: $applicantId) {
      applicantId
      applicantFirstName
      applicantLastName
      companyId
      companyName
      employeeId
    }
  }
`

export const GET_APPLICANT_METRICS = gql`
  query ApplicantMetrics($groupBy: String!, $filters: ApplicantFiltersInput) {
    applicantMetrics(groupBy: $groupBy, filters: $filters) {
      totalCount
      items {
        name
        value
      }
    }
  }
`

export const CREATE_APPLICANT = gql`
  mutation createApplicant($input: CreateApplicantInput!) {
    createApplicant(input: $input) {
      applicantId
    }
  }
`

export const CREATE_BASIC_APPLICANT = gql`
  mutation createBasicApplicant($input: CreateBasicApplicantInput!) {
    createBasicApplicant(input: $input) {
      applicantId
      applicationId
      segment
    }
  }
`

export const UPDATE_APPLICANT = gql`
  mutation updateApplicant($id: String!, $input: UpdateApplicantInput!) {
    updateApplicant(id: $id, input: $input) {
      applicant {
        applicantId
      }
      warning
    }
  }
`

export const UPDATE_APPLICANT_PHONE = gql`
  mutation updateApplicantPhone($id: String!, $phone: String!) {
    updateApplicantPhone(id: $id, phone: $phone) {
      applicantId
    }
  }
`

export const DOWNLOAD_CSV = gql`
  mutation DownloadCSV(
    $filters: ApplicantFiltersInput
    $pageInfo: PageInfoInput
  ) {
    downloadCSV(filters: $filters, pageInfo: $pageInfo) {
      fileContent
      fileName
    }
  }
`

export const BULK_STAGE_UPDATE = gql`
  mutation BulkStageUpdate(
    $applicants: [String!]!
    $input: ApplicantStageInput!
  ) {
    bulkStageUpdate(applicants: $applicants, input: $input) {
      applicants {
        applicantId
        success
        errorMsg
      }
    }
  }
`

export const BULK_RECRUITER_ASSIGNMENT = gql`
  mutation BulkRecruiterAssignment(
    $applicants: [String!]!
    $recruiterId: String!
  ) {
    bulkRecruiterAssignment(
      applicants: $applicants
      recruiterId: $recruiterId
    ) {
      applicants {
        applicantId
        success
        errorMsg
      }
    }
  }
`

export const BULK_MEMBER_ASSIGNMENT = gql`
  mutation BulkMemberAssignment(
    $applicants: [String!]!
    $memberEmployeeIds: [String!]!
  ) {
    bulkMemberAssignment(
      applicants: $applicants
      memberEmployeeIds: $memberEmployeeIds
    ) {
      applicants {
        applicantId
        success
        errorMsg
      }
    }
  }
`

export const UPDATE_APPLICANT_COMPANY_TAGS = gql`
  mutation updateApplicantCompanyTags(
    $applicantId: String!
    $tags: [String!]!
  ) {
    updateApplicantCompanyTags(applicantId: $applicantId, tags: $tags) {
      applicantId
    }
  }
`

export const ARCHIVE_APPLICANT = gql`
  mutation ArchiveApplicant($id: String!) {
    archiveApplicant(id: $id) {
      applicantId
      status
      archivedAt
    }
  }
`

export const UNARCHIVE_APPLICANT = gql`
  mutation UnarchiveApplicant($id: String!) {
    unarchiveApplicant(id: $id) {
      applicantId
      status
    }
  }
`

export const GET_APPLICANT_RECRUITER_BY_ID = gql`
  query ApplicantRecruiter($id: String!) {
    applicant(id: $id) {
      recruiter {
        employeeId
      }
    }
  }
`
